import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { teamApi } from "@/lib/api";
import { useAppStore } from "@/store/useAppStore";
import { teamErrorMessage } from "@/lib/teamErrors";

function roleRank(role) {
  return { viewer: 0, member: 1, admin: 2, owner: 3 }[role] ?? 0;
}

export function useTeamSettings() {
  const { auth, tenant } = useAppStore();
  const currentRole = auth?.server?.role || "member";
  const currentUserId = auth?.server?.user_id;
  const currentPlan = auth?.server?.plan || "trial";

  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [roleDialog, setRoleDialog] = useState(null);
  const [roleValue, setRoleValue] = useState("member");
  const [roleBusy, setRoleBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const canInvite = roleRank(currentRole) >= roleRank("admin");
  const canManageMembers = currentRole === "owner";
  const planAllowsInvite = currentPlan !== "trial";
  const workspaceLabel = tenant?.name || "Primary workspace";

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const data = await teamApi.listMembers();
      setMembers(data.members || []);
      setPendingInvites(data.pending_invites || []);
    } catch (err) {
      setMembers([]);
      setPendingInvites([]);
      toast.error(teamErrorMessage(err, "Could not load team members"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const sendInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid work email");
      return;
    }
    setInviteBusy(true);
    try {
      await teamApi.invite(email, inviteRole);
      toast.success("Invite sent");
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      await loadTeam();
    } catch (err) {
      toast.error(teamErrorMessage(err, "Failed to send invite"));
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    setRevokingId(inviteId);
    try {
      await teamApi.revokeInvite(inviteId);
      toast.success("Invite revoked");
      await loadTeam();
    } catch (err) {
      toast.error(teamErrorMessage(err, "Could not revoke invite"));
    } finally {
      setRevokingId(null);
    }
  };

  const openRoleDialog = (member) => {
    setRoleDialog(member);
    setRoleValue(member.role || "member");
  };

  const saveRole = async () => {
    if (!roleDialog) return;
    setRoleBusy(true);
    try {
      await teamApi.changeRole(roleDialog.user_id, roleValue);
      toast.success("Role updated");
      setRoleDialog(null);
      await loadTeam();
    } catch (err) {
      toast.error(teamErrorMessage(err, "Could not update role"));
    } finally {
      setRoleBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      await teamApi.removeMember(removeTarget.user_id);
      toast.success("Member removed");
      setRemoveTarget(null);
      await loadTeam();
    } catch (err) {
      toast.error(teamErrorMessage(err, "Could not remove member"));
    } finally {
      setRemoveBusy(false);
    }
  };

  const inviteDisabledReason = useMemo(() => {
    if (!canInvite) return "Only owners and admins can invite teammates.";
    if (!planAllowsInvite) return "Upgrade to Founder plan or above to invite teammates.";
    return null;
  }, [canInvite, planAllowsInvite]);

  return {
    members,
    pendingInvites,
    loading,
    inviteOpen,
    setInviteOpen,
    inviteEmail,
    setInviteEmail,
    inviteRole,
    setInviteRole,
    inviteBusy,
    roleDialog,
    setRoleDialog,
    roleValue,
    setRoleValue,
    roleBusy,
    removeTarget,
    setRemoveTarget,
    removeBusy,
    revokingId,
    canInvite,
    canManageMembers,
    workspaceLabel,
    currentUserId,
    sendInvite,
    revokeInvite,
    openRoleDialog,
    saveRole,
    confirmRemove,
    inviteDisabledReason,
  };
}
